import { memo } from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup as MUIRadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Box,
  type FormControlProps,
} from "@mui/material";
import TruncatedText from "@/components/Truncate/TruncatedText";

type OptionType = {
  [key: string]: any;
};

interface RadioProps<T extends OptionType> {
  field: {
    value: any;
    onChange: (value: any) => void;
    onBlur?: () => void;
    name: string;
  };
  label?: string;
  required?: boolean;
  options: T[];
  idKey?: keyof T;
  nameKey?: keyof T;
  row?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  slotProps?: FormControlProps;
  maxLength?: number;
}

const RadioGroup = <T extends OptionType>({
  field,
  label,
  required = false,
  options,
  idKey = "value",
  nameKey = "label",
  row = true,
  disabled = false,
  error = false,
  helperText,
  slotProps,
  maxLength,
}: RadioProps<T>) => {
  return (
    <FormControl
      component="fieldset"
      disabled={disabled}
      error={error}
      {...slotProps}
    >
      <Box display="flex" alignItems="flex-start" gap={2}>
        {label &&
          <FormLabel
            required={required}
            sx={{
              mt: 2,
              color: "rgba(0,0,0,0.6)",
              minWidth: "fit-content",
              whiteSpace: "nowrap",
              "&.Mui-focused": {
                color: "rgba(0,0,0,0.6)",
              },
            }}
          >
            {maxLength ? <TruncatedText limit={maxLength} value={label} /> : label}
          </FormLabel>}

        <MUIRadioGroup
          row={row}
          value={field.value ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            field.onChange(e.target.value)
          }
          onBlur={field.onBlur}
          name={field.name}
          sx={{
            mt: 1,
          }}
        >
          {options?.map((opt) => (
            <FormControlLabel
              key={String(opt?.[idKey])}
              value={opt?.[idKey]}
              control={<Radio />}
              label={String(opt?.[nameKey])}
            />
          ))}
        </MUIRadioGroup>
      </Box>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

export default memo(RadioGroup);